/**
 * Routing helpers for ESS/Admin desk requests that should go to Reporting Manager
 * (Change Shift, etc.), with HR/Admin fallback when the requester has no RM.
 * Attendance Lock/Unlock may route to a custom workflow role (e.g. Client Access).
 */

import { isManagerRole } from "../utils/workflowStepsBadge";
import { normalizeRoleCodeForMatch } from "../utils/roleSelectOptions";

export const RM_ROUTED_ACTION_LABELS = new Set([
  "Change Shift",
  "Change WO",
  "Regularise checks",
  "Short Leave",
  "On Duty",
  "Comp Off",
  "Comp Off Work",
  "Comp Off Credit",
  "Overtime",
  "Restricted Holiday",
  "Status change request",
  "Attendance Lock",
  "Attendance Unlock",
  "Attendance Finalization",
]);

/**
 * @param {object|null} employee - getEmployee payload
 * @returns {number|null}
 */
export function extractReportingManagerEmployeeId(employee) {
  if (!employee || typeof employee !== "object") return null;
  const raw =
    employee.reportingManagerId ??
    employee.reporting_manager_id ??
    employee.reportingManager?.id ??
    employee.reportingManager?.employeeId ??
    null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function roleCodesMatch(a, b) {
  const left = String(a || "").trim();
  const right = String(b || "").trim();
  if (!left || !right) return false;
  if (left.toLowerCase() === right.toLowerCase()) return true;
  return normalizeRoleCodeForMatch(left) === normalizeRoleCodeForMatch(right);
}

/**
 * Whether the signed-in user may act on this request in the Approvals desk.
 * Own requests never appear here. RM-routed items only show to that employee's RM;
 * custom-role workflow steps (e.g. Client Access) show to users with that role;
 * no-RM / HR queue items show to Admin/Super Admin.
 */
export function canActOnRmRoutedApproval(
  request,
  { employeeId, isAdmin, customRoleCode } = {}
) {
  if (!request) return false;
  const myId = Number(employeeId);
  const requesterId = Number(request.employeeId);
  const isFinalization =
    request.actionLabel === "Attendance Lock" ||
    request.actionLabel === "Attendance Unlock" ||
    request.actionLabel === "Attendance Finalization";

  if (!isFinalization && Number.isFinite(myId) && Number.isFinite(requesterId) && myId === requesterId) {
    return false;
  }

  // Manager already raised on behalf of reportee → HR/Admin acts next.
  if (request.source === "my_team" || request.raisedByManager) {
    return !!isAdmin;
  }

  // Attendance finalization workflow may assign a custom role (not RM).
  if (isFinalization) {
    const pendingRole = String(
      request.pendingRole ||
        request.formData?.workflowPendingRole ||
        ""
    ).trim();
    if (pendingRole && !isManagerRole(pendingRole)) {
      if (isAdmin) return true;
      return roleCodesMatch(customRoleCode, pendingRole);
    }
  }

  // Company Admin / Super Admin may act on any RM-routed desk request
  // (Regularise, OD, leave-style cancellation, …), except their own.
  if (isAdmin) return true;

  const rmId = Number(request.reportingManagerEmployeeId);
  if (Number.isFinite(rmId) && rmId > 0) {
    return Number.isFinite(myId) && myId === rmId;
  }

  // No reporting manager (or legacy request without RM id) → HR / Admin queue only.
  if (
    request.noReportingManager ||
    request.pendingWith === "HR" ||
    request.pendingWith === "Admin" ||
    !request.reportingManagerEmployeeId
  ) {
    return !!isAdmin;
  }

  return false;
}

export function isRmRoutedActionLabel(actionLabel) {
  const raw = String(actionLabel || "").trim();
  if (RM_ROUTED_ACTION_LABELS.has(raw)) return true;
  if (raw.endsWith(" cancellation")) {
    return RM_ROUTED_ACTION_LABELS.has(raw.slice(0, -" cancellation".length));
  }
  return false;
}
